(() => {
  if (window.rdbReviewsInitialized) return;
  window.rdbReviewsInitialized = true;

  const initialized = new WeakSet();
  const observedDrawers = new WeakSet();
  const reviewDialogs = new WeakMap();
  const getRows = root => [...root.querySelectorAll('[data-review-row]')];
  const refresh = (root, announce = false) => {
    const rows = getRows(root);
    if (!initialized.has(root)) {
      rows.forEach((row, index) => { row.hidden = index >= 5; });
      initialized.add(root);
    }
    const visible = rows.filter(row => !row.hidden).length;
    const loadButton = root.querySelector('[data-review-load]');
    if (loadButton) loadButton.hidden = visible >= rows.length;
    const progress = root.querySelector('[data-review-progress]');
    if (announce && progress && root.dataset.reviewProgressTemplate) {
      progress.textContent = root.dataset.reviewProgressTemplate
        .replaceAll('{visible}', String(visible))
        .replaceAll('{total}', String(rows.length));
    }
  };
  const createStars = rating => {
    const stars = document.createElement('span');
    stars.className = 'rdb-review-stars rdb-rating__stars';
    stars.setAttribute('aria-hidden', 'true');
    for (let index = 0; index < 5; index += 1) {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('focusable', 'false');
      ['outline', 'fill'].forEach(kind => {
        const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path.setAttribute('class', `rdb-review-stars__${kind}`);
        path.setAttribute('d', 'm12 3 2.78 5.63L21 9.54l-4.5 4.39 1.06 6.2L12 17.2l-5.56 2.93 1.06-6.2L3 9.54l6.22-.91Z');
        if (kind === 'fill') path.style.clipPath = `inset(0 ${100 - Math.min(1, Math.max(0, rating - index)) * 100}% 0 0)`;
        svg.append(path);
      });
      stars.append(svg);
    }
    return stars;
  };
  const initializeWishlist = () => {
    const drawer = document.querySelector('#rosarium-wishlist-drawer');
    const source = document.querySelector('[data-review-index]');
    if (!drawer || !source || observedDrawers.has(drawer)) return;
    let index;
    try { index = JSON.parse(source.textContent); } catch { return; }
    if (!index.products || typeof index.label !== 'string') return;
    const addRatings = () => {
      drawer.querySelectorAll('.rosarium-wishlist-drawer__item[data-product-id]').forEach(item => {
        const details = item.querySelector('.rosarium-wishlist-drawer__details');
        if (!details || details.querySelector('.rdb-rating')) return;
        const counts = index.products[item.dataset.productId]?.counts;
        if (!Array.isArray(counts) || counts.length !== 5 || !counts.every(count => Number.isInteger(count) && count >= 0)) return;
        const count = counts.reduce((total, value) => total + value, 0);
        if (!count) return;
        const points = counts.reduce((total, value, starIndex) => total + value * (starIndex + 1), 0);
        const rating = (Math.floor((points * 20 + count) / (count * 2)) / 10).toFixed(1);
        const row = document.createElement('span');
        row.className = 'rdb-rating';
        row.dataset.reviewProduct = item.dataset.productId;
        row.dataset.reviewCount = String(count);
        row.dataset.reviewRating = rating;
        row.setAttribute('role', 'img');
        row.setAttribute('aria-label', index.label.replaceAll('__RATING__', rating).replaceAll('__COUNT__', String(count)));
        row.append(createStars(Number(rating)));
        [['average', rating], ['count', `(${count})`]].forEach(([kind, text]) => {
          const span = document.createElement('span');
          span.className = `rdb-rating__${kind}`;
          span.setAttribute('aria-hidden', 'true');
          span.textContent = text;
          row.append(span);
        });
        const anchor = details.querySelector('.rosarium-wishlist-drawer__price, .rosarium-wishlist-drawer__product-title');
        const price = details.querySelector('.rosarium-wishlist-drawer__price');
        (price || anchor)?.after(row);
      });
    };
    const observer = new MutationObserver(records => {
      const changedItems = records.some(record => [...record.addedNodes].some(node => node instanceof Element && (node.matches('.rosarium-wishlist-drawer__item') || node.querySelector('.rosarium-wishlist-drawer__item'))));
      if (changedItems) addRatings();
    });
    observer.observe(drawer, { childList: true, subtree: true });
    observedDrawers.add(drawer);
    addRatings();
  };
  const initializeReviewForm = root => {
    const compose = root.querySelector('[data-review-compose]');
    const dialog = compose?.querySelector('[data-review-dialog]');
    const trigger = compose?.querySelector('[data-review-write]');
    if (!compose || !dialog || !trigger || reviewDialogs.has(root) || typeof dialog.showModal !== 'function') return;
    const form = compose.closest('form');
    let dismissTimer;
    const scheduleDismiss = () => {
      clearTimeout(dismissTimer);
      if (dialog.open && dialog.dataset.reviewResult === 'success' && !document.hidden) {
        dismissTimer = setTimeout(() => dialog.close(), 2600);
      }
    };
    const open = () => {
      if (dialog.open) return;
      compose.open = true;
      dialog.showModal();
      trigger.setAttribute('aria-expanded', 'true');
      dialog.scrollTop = 0;
      const focusTarget = dialog.querySelector('[data-review-result-focus]') || dialog.querySelector('#RdbReviewTitle');
      focusTarget?.focus({ preventScroll: true });
      scheduleDismiss();
    };
    dialog.removeAttribute('open');
    compose.open = false;
    compose.dataset.reviewEnhanced = '';
    trigger.setAttribute('aria-haspopup', 'dialog');
    trigger.setAttribute('aria-controls', dialog.id);
    trigger.setAttribute('aria-expanded', 'false');
    dialog.querySelectorAll('[data-review-close]').forEach(button => {
      button.hidden = false;
      button.addEventListener('click', () => dialog.close());
    });
    document.addEventListener('visibilitychange', scheduleDismiss);
    dialog.addEventListener('close', () => {
      clearTimeout(dismissTimer);
      const wasSuccessful = dialog.dataset.reviewResult === 'success';
      if (wasSuccessful) {
        const receipt = dialog.querySelector('[data-review-success]');
        if (receipt) {
          receipt.className = 'rdb-review-form__receipt';
          receipt.removeAttribute('tabindex');
          receipt.removeAttribute('data-review-result-focus');
          compose.after(receipt);
        }
        dialog.dataset.reviewResult = '';
        dialog.querySelector('[data-review-success-close]')?.remove();
        dialog.querySelector('#RdbReviewTitle').textContent = trigger.textContent;
        dialog.querySelector('[data-review-fields]').hidden = false;
        form.reset();
      }
      compose.open = false;
      trigger.setAttribute('aria-expanded', 'false');
      if (wasSuccessful) root.scrollIntoView({ block: 'start', behavior: 'instant' });
      trigger.focus({ preventScroll: true });
    });
    let backdropPress = false;
    const outside = event => {
      const bounds = dialog.getBoundingClientRect();
      return event.target === dialog && (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom);
    };
    dialog.addEventListener('pointerdown', event => { backdropPress = outside(event); });
    dialog.addEventListener('click', event => {
      if (backdropPress && outside(event)) dialog.close();
      backdropPress = false;
    });
    reviewDialogs.set(root, open);
    if (dialog.dataset.reviewResult) {
      if (dialog.dataset.reviewResult === 'success') {
        const current = new URL(location.href);
        current.searchParams.delete('contact_posted');
        current.hash = 'product-reviews';
        history.replaceState(history.state, '', current.pathname + current.search + current.hash);
        root.scrollIntoView({ block: 'start', behavior: 'instant' });
      }
      open();
    }
  };
  const initialize = () => {
    document.querySelectorAll('#product-reviews').forEach(root => {
      refresh(root);
      initializeReviewForm(root);
    });
    initializeWishlist();
  };

  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-review-load], [data-review-write]');
    const root = button?.closest('#product-reviews');
    if (!button || !root) return;
    if (button.hasAttribute('data-review-write')) {
      const open = reviewDialogs.get(root);
      if (open) {
        event.preventDefault();
        open();
      }
      button.dispatchEvent(new CustomEvent('rdb:review:write', { bubbles: true, detail: { productId: root.dataset.reviewProduct } }));
      return;
    }
    const nextRows = getRows(root).filter(row => row.hidden).slice(0, 5);
    nextRows.forEach(row => { row.hidden = false; });
    refresh(root, true);
    nextRows[0]?.focus();
  });
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('rdb:reviews:init', initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
